import type {WizardData} from '../index'
import type {
  AiAllocationAction,
  AiAllocationContext,
  LessonPlanningData,
} from '../types/teacherAllocationAi'
import {findTeacherByRef, getTeacherFullName, parseAllocationSlotKey} from './allocationIds'

export type MapActionsParams = {
  actions: AiAllocationAction[]
  context: AiAllocationContext
  lessonPlanningData: LessonPlanningData
  manualData: WizardData['manualData']
  defaultFrequency: string
  defaultDuration: string
}

export function mapActionsToLessonPlanningData(params: MapActionsParams): LessonPlanningData {
  const {actions, lessonPlanningData, manualData, defaultFrequency, defaultDuration} = params

  const teachers = manualData.teachers ?? []
  const next: LessonPlanningData = {...lessonPlanningData}

  actions.forEach((action) => {
    const {classKey, subjectName} = parseAllocationSlotKey(action.slotKey)
    if (!classKey || !subjectName) return

    const existing = next[classKey]?.[subjectName]
    const nextSubjectMap = {...(next[classKey] ?? {})}

    if (action.type === 'remove') {
      // Not taught: the cell goes, like a manual "Mark as not taught"; the caller marks the slot
      // deleted (see getRemovedSlotKeys).
      delete nextSubjectMap[subjectName]
    } else if (action.type === 'set_frequency' || action.type === 'set_duration') {
      // Settings only: the teacher and the other setting are kept as they are.
      nextSubjectMap[subjectName] = {
        teacher: existing?.teacher || '',
        teacherEmployeeId: existing?.teacherEmployeeId || '',
        frequency:
          (action.type === 'set_frequency' && action.frequency) ||
          existing?.frequency ||
          defaultFrequency,
        duration:
          (action.type === 'set_duration' && action.duration) ||
          existing?.duration ||
          defaultDuration,
      }
    } else if (action.type === 'unassign') {
      nextSubjectMap[subjectName] = {
        teacher: '',
        teacherEmployeeId: '',
        frequency: existing?.frequency || defaultFrequency,
        duration: existing?.duration || defaultDuration,
      }
    } else {
      const teacher = findTeacherByRef(teachers, action.teacherEmployeeId, action.teacherName)
      nextSubjectMap[subjectName] = {
        teacher: teacher ? getTeacherFullName(teacher) : existing?.teacher || '',
        teacherEmployeeId: teacher ? String(teacher.employeeId ?? '').trim() : '',
        frequency: existing?.frequency || defaultFrequency,
        duration: existing?.duration || defaultDuration,
      }
    }

    next[classKey] = nextSubjectMap
  })

  return next
}

export function getPreviewSlotKeys(actions: AiAllocationAction[]): Set<string> {
  return new Set(actions.map((a) => a.slotKey).filter(Boolean))
}

/** Slots an action list marks as not taught. */
export function getRemovedSlotKeys(actions: AiAllocationAction[]): string[] {
  return actions.filter((a) => a.type === 'remove' && a.slotKey).map((a) => a.slotKey)
}
